"use client";

import dynamic from "next/dynamic";
import type { EducationSection } from "@/lib/content/education";
import { renderInlineFinanceText } from "./renderInlineFinanceText";

// Chart.js must not execute during static-build SSR — same pattern as the
// comparison education sections and the live results chart.
const SemanticChart = dynamic(() => import("@/components/ui/SemanticChart"), {
  ssr: false,
});

interface CalculatorEducationProps {
  sections: EducationSection[];
}

/**
 * The teaching layer of a calculator page: short pain-point-first sections
 * with data charts interleaved where the explanation needs them. Paragraphs
 * are prerendered HTML (SEO); only the chart canvas hydrates client-side.
 */
export default function CalculatorEducation({ sections }: CalculatorEducationProps) {
  if (!sections || sections.length === 0) return null;

  return (
    <section className="calc-education-section">
      {/* h3/h4: this whole block renders inside the layout's h2 "Guide & How-To"
          section, so its headings must sit one level below it (was h2/h3,
          which broke the document outline with a second page-level heading). */}
      <h3 className="calc-education-title">What the numbers actually mean for you</h3>
      {sections.map((sec, idx) => (
        <div key={idx} className="calc-education-block">
          <h4 className="calc-education-heading">{sec.heading}</h4>
          {sec.paragraphs.map((p, pIdx) => (
            <p key={pIdx} className="calc-education-paragraph">
              {renderInlineFinanceText(p)}
            </p>
          ))}
          {sec.chart && <SemanticChart chartData={sec.chart} />}
        </div>
      ))}
      <style jsx>{`
        .calc-education-section {
          margin-bottom: var(--space-8);
        }
        .calc-education-title {
          font-family: var(--font-heading);
          font-size: var(--text-xl);
          color: var(--text-primary);
          margin-bottom: var(--space-5);
        }
        .calc-education-block {
          margin-bottom: var(--space-6);
        }
        .calc-education-heading {
          font-family: var(--font-heading);
          font-size: var(--text-lg);
          color: var(--text-primary);
          margin-bottom: var(--space-3);
        }
        .calc-education-paragraph {
          color: var(--text-secondary);
          line-height: 1.6;
          font-size: var(--text-base);
          margin-bottom: var(--space-3);
        }
      `}</style>
    </section>
  );
}
